/**
 * Minimum distance to maintain between the context menu and viewport edges.
 */
const VIEWPORT_PADDING = 8;

/**
 * Distance to offset the menu from the click position (prevents cursor overlap).
 */
const OFFSET_FROM_CLICK = 8;

/**
 * Estimated width of the context menu for positioning calculations.
 */
const MENU_ESTIMATED_WIDTH = 242;

/**
 * Estimated height of the context menu for positioning calculations.
 */
const MENU_ESTIMATED_HEIGHT = 300;

/**
 * Computes the optimal position for a context menu to ensure it stays within viewport bounds.
 *
 * The menu is positioned offset from the click point, preferring to appear:
 * - To the right and below the click (default)
 * - To the left if there's insufficient space on the right
 * - Above if there's insufficient space below
 *
 * @param position - The click position (x, y coordinates)
 * @param viewport - Optional viewport dimensions (defaults to window.innerWidth/innerHeight)
 * @returns The computed top-left position for the menu
 */
export function computeMenuPosition(
  position: { x: number; y: number },
  viewport?: { width: number; height: number }
): { x: number; y: number } {
  const { x, y } = position;
  const viewportWidth = viewport?.width ?? window.innerWidth;
  const viewportHeight = viewport?.height ?? window.innerHeight;

  const anchorRight = x + OFFSET_FROM_CLICK;
  const anchorBelow = y + OFFSET_FROM_CLICK;
  const fitsRight =
    anchorRight + MENU_ESTIMATED_WIDTH + VIEWPORT_PADDING <= viewportWidth;
  const fitsBelow =
    anchorBelow + MENU_ESTIMATED_HEIGHT + VIEWPORT_PADDING <= viewportHeight;

  return {
    x: fitsRight ? anchorRight : x - OFFSET_FROM_CLICK - MENU_ESTIMATED_WIDTH,
    y: fitsBelow ? anchorBelow : y - OFFSET_FROM_CLICK - MENU_ESTIMATED_HEIGHT
  };
}
